'use client';
import React, { useEffect, useState } from 'react';
import { Badge, Button, classNames, Div, Divider, Popper, PopperContent, PopperHandler, Skeleton, Text } from '@pezeshk-book/ui-kit';
import Image from '@elements/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/root-reducer';
import { AuthActions } from '@store/auth/auth-actions';
import routes from '@routes';
import PhoneWhiteIcon from '@icons-components/phone-white-icon';
import LoginIcon from '@icons-components/login-icon';
import PersonIcon from '@icons-components/person-icon';
import AddToBasketIcon from '@icons-components/add-to-basket-icon';
import process from 'process';
import GeneralSearch from '@layouts/general-search';
import CanadaFlagIcon from '@icons-components/canada-flag-icon';

const servicesLinks = [
  {
    slug: 'labs',
    title: 'آزمایشگاه در محل',
    url: routes['route.labs.index'],
    isActive: process.env.LABORATORY_SERVICE_ACTIVE === 'true',
  },
  {
    slug: 'consult',
    title: 'مشاوره آنلاین پزشکی',
    url: routes['route.users.index'],
    isActive: process.env.CONSULT_SERVICE_ACTIVE === 'true',
  },
  {
    slug: 'pharmacy',
    title: 'داروخانه آنلاین',
    url: routes['route.pharmacies.index'],
    isActive: process.env.PHARMACY_SERVICE_ACTIVE === 'true',
  },
  {
    slug: 'home-care',
    title: 'خدمات پزشکی در محل',
    url: routes['route.home-care.index'],
    isActive: process.env.HOME_CARE_SERVICE_ACTIVE === 'true',
  },
  {
    slug: 'appointment',
    title: 'نوبت دهی آنلاین',
    url: routes['route.users.index'],
    isActive: process.env.APPOINTMENT_SERVICE_ACTIVE === 'true',
  },
];

const headerLinks = [
  {
    slug: 'home',
    title: 'خانه',
    url: '/',
    isActive: true,
  },
  {
    slug: 'labs',
    title: 'خدمات',
    url: '/services',
    children: servicesLinks,
    isActive: true,
  },
  {
    slug: 'products',
    title: 'محصولات',
    url: routes['route.products.index'],
    isActive: process.env.PRODUCT_SERVICE_ACTIVE === 'true',
  },
  {
    slug: 'posts',
    title: 'مجله سلامت',
    url: '/posts',
    isActive: true,
  },
  {
    slug: 'faq',
    title: 'سوالات متداول',
    url: '/faq',
    isActive: true,
  },
  {
    slug: 'about-us',
    title: 'درباره ما',
    url: '/about-us',
    isActive: true,
  },
  {
    slug: 'contacts',
    title: 'تماس با ما',
    url: '/contacts',
    isActive: true,
  },
];

const DesktopHeader = () => {
  const pathname = usePathname();
  const [popper, setPopper] = useState<boolean>(false);

  const handleMouseOver = (event: any) => {
    setPopper(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setPopper(false);
  };

  const getMenuButtons = () => {
    return headerLinks.map(({ title, url, isActive, children }: { title: string, url: string, isActive: boolean, children?: Array<{ title: string, url: string, isActive: boolean }> }) => {
      if (children && children.length > 0 && pathname) {
        const isCurrent = url !== '/' && pathname.indexOf(url) !== -1;
        return (
          <Div className={''} key={title} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
            <Popper showPopper={popper} position={'bottom-right'} handlePopper={() => setPopper(false)}>
              <PopperHandler>
                <Link passHref={true} key={title} href={url} className={'pointer px-2 flex-col items-end relative mx-2 justify-between'}>
                  <Text color={pathname.includes('users') ? 'tertiary' : isCurrent ? 'tertiary' : 'grey.900'} type={'bold'} typography={'sm'}>{title}</Text>
                </Link>
              </PopperHandler>
              <PopperContent>
                <Div className={'w-max flex-col rounded-2xl translate-y-8 p-5 bg-white shadow-lg gap-y-2 items-center'}>
                  {children && children.map((item, index) => {
                    if (!item.isActive) {
                      return null;
                    }
                    return (
                      <React.Fragment key={item.title}>
                        <Link
                          passHref={true}
                          className={'w-full flex-col items-end relative mx-2 justify-between'}
                          href={item.url}>
                          <Text color={pathname.includes(item.url) ? 'tertiary' : 'grey.900'} type={'bold'} typography={'sm'}>{item.title}</Text>
                        </Link>
                        {children.length !== index + 1 ? (
                          <Divider color={'control'}/>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </Div>
              </PopperContent>
            </Popper>
          </Div>
        );
      }

      const isCurrent = pathname === '/' && url === '/' ? true : url !== '/' && pathname && pathname.indexOf(url) !== -1;
      if (isActive) {
        return (
          <Link
            passHref={true}
            className={'pointer px-1 flex-col items-end relative mx-2 justify-between'}
            key={title}
            href={url}>
            <Text color={isCurrent ? 'tertiary' : 'grey.900'} type={'bold'} typography={'sm'}>{title}</Text>
          </Link>
        );
      }

      return null;
    });
  };

  return (
    <Div className={'w-full items-center justify-center'}>
      <Div className={'bg-white h-20 w-full shadow-2xl rounded-2xl px-5 items-center justify-between'}>
        <Div className={'items-center gap-4'}>
          <Button
            className={'!p-0'}
            variant={'text'}
            startAdornment={<CanadaFlagIcon/>}
            href={'https://pbmeds.com'}
            size={'huge'}/>
          <Link passHref={true} href={'/'}>
            <Div className={'relative w-40 h-6'}>
              <Image sizes={'20vw'} src={'/images/header/logo.svg'} fill={true} alt={'پزشک بوک'}/>
            </Div>
          </Link>
        </Div>
        <Div className={'justify-between items-center self-center gap-1'}>
          {getMenuButtons()}
        </Div>
        <Div className={'relative gap-4'}>
          <UserInfo/>
          <a href={'tel:+982191004191'}>
            <Button size={'small'} startAdornment={<PhoneWhiteIcon/>} color={'tertiary'}>
            021-91004191
            </Button>
          </a>
          <BasketButton>
            <Button
              size={'small'}
              href={routes['route.basket.index']}
              shape={'square'}
              variant={'outlined'}
              startAdornment={<AddToBasketIcon/>}
              color={'control'}
            />
          </BasketButton>
          <GeneralSearch />
        </Div>
      </Div>
    </Div>
  );
};

export default DesktopHeader;

export const UserInfo = ({ isResponsive }: { isResponsive?: boolean }) => {
  const dispatch = useDispatch();
  const [domLoaded, setDomLoaded] = useState(false);
  useEffect(() => {
    setDomLoaded(true);
  }, []);

  const { userInfo, isLoggedIn, showInfoLoading } = useSelector((state: RootState) => state.auth);

  if (domLoaded && !showInfoLoading && !userInfo && !isLoggedIn) {
    return (
      <Button
        className={'w-40'}
        size={isResponsive ? 'medium' : 'small'}
        variant={'outlined'}
        startAdornment={<LoginIcon/>}
        onClick={() => dispatch(AuthActions.setLoginModal({ loginModal: true }))}>
        ورود / ثبت نام
      </Button>
    );
  }

  if (isLoggedIn) {
    return (
      <Button
        className={'w-40'}
        size={isResponsive ? 'medium' : 'small'}
        href={'/client-dashboard'}
        variant={'outlined'}
        startAdornment={<PersonIcon/>}
        color={'primary'}>
        <Text
          type={'bold'}
          color={'inherit'}
          className={classNames('whitespace-nowrap text-ellipsis overflow-hidden', isResponsive ? 'text-m-sm sm:text-d-sm md:text-d-base' : 'text-m-xs sm:text-t-xs md:text-d-sm')}
        >
          {userInfo}
        </Text>
      </Button>
    );
  }

  return (
    <Skeleton
      className={'!w-40 rounded-lg !max-w-full'}
      shape={'rectangular'}
      size={isResponsive ? 'medium' : 'small'}
      color={'control'}/>
  );
};

export const BasketButton = ({ children }: any) => {
  const { basketCount } = useSelector((state: RootState) => state.basket);

  if (process.env.PRODUCT_SERVICE_ACTIVE === 'true' && basketCount) {
    return (
      <Badge size={'sm'} color={'danger'} anchorOrigin={{ horizontal: 'left', vertical: 'top' }} badgeContent={basketCount}>
        {children}
      </Badge>
    );
  }

  return null;
};
